/**
 * Planner model (§13 / §13a / §17 / §19) — the level hierarchy, zoom regimes,
 * plan-vs-actual filter, and pure helpers that turn a PlanTimelineResponse into
 * the derived facts the Plotly Gantt needs (time span, Today position, scale).
 *
 * Kept framework-free so the transforms are unit-testable without a DOM.
 */
import type { PlanTimelineResponse, PlanTimelineBar, PlanTimelineTrack } from "@api/types";

/**
 * The planning hierarchy (§19: Corporate → Regional → Local → Fleet → Pad →
 * Well → Section). Which levels are reachable is org- + role-gated; the switcher
 * only shows the levels the current scope resolves. Fleet is the default entry.
 */
export type PlannerLevel =
  | "corporate"
  | "regional"
  | "local"
  | "fleet"
  | "pad"
  | "well"
  | "section";

/** Zoom regimes (§17) — Day…Year, plus auto (fit the visible data span). */
export type ZoomScale = "day" | "week" | "month" | "quarter" | "year";

/** Plan-vs-actual display filter (§13). */
export type PlanActualMode = "both" | "plan" | "actual";

/** The levels the Planner ships wired to a plan-timeline endpoint (§10.9). */
export const WIRED_LEVELS: PlannerLevel[] = ["fleet", "pad", "well", "section"];

/** Ordered levels, coarsest → finest, for the switcher + breadcrumb. */
export const LEVEL_ORDER: PlannerLevel[] = [
  "corporate",
  "regional",
  "local",
  "fleet",
  "pad",
  "well",
  "section",
];

export const ZOOM_SCALES: ZoomScale[] = ["day", "week", "month", "quarter", "year"];

/** A parsed bar with resolved Date spans; nulls dropped by the caller. */
export interface DatedSpan {
  start: number;
  end: number;
}

/** Parse an ISO date/datetime string to epoch ms, or null. */
export function toMs(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** The planned span of a bar, if both ends are present. */
export function plannedSpan(bar: PlanTimelineBar): DatedSpan | null {
  const start = toMs(bar.planned_start);
  const end = toMs(bar.planned_end);
  return start != null && end != null ? { start, end } : null;
}

/**
 * The actual span of a bar. An open actual (started, no end) is clamped to
 * `now` so an in-progress operation still draws to the Today line.
 */
export function actualSpan(bar: PlanTimelineBar, now: number): DatedSpan | null {
  const start = toMs(bar.actual_start);
  if (start == null) return null;
  const end = toMs(bar.actual_end) ?? now;
  return { start, end: Math.max(end, start) };
}

/**
 * The overall [min, max] epoch span across every plan + actual bar in the
 * response (plus `now`, so the Today line is always in view). Returns null when
 * the response has no dated bars.
 */
export function timelineSpan(
  resp: PlanTimelineResponse | undefined,
  now: number,
): DatedSpan | null {
  if (!resp) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const track of resp.tracks) {
    for (const bar of track.bars) {
      for (const span of [plannedSpan(bar), actualSpan(bar, now)]) {
        if (!span) continue;
        if (span.start < min) min = span.start;
        if (span.end > max) max = span.end;
      }
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  min = Math.min(min, now);
  max = Math.max(max, now);
  return { start: min, end: max };
}

const DAY = 86_400_000;

/**
 * Auto-pick a zoom scale from a span length (§17: "auto-pick scale from the
 * visible span"). Short campaigns → day/week; life-of-well → quarter/year.
 */
export function autoScale(span: DatedSpan): ZoomScale {
  const days = (span.end - span.start) / DAY;
  if (days <= 10) return "day";
  if (days <= 60) return "week";
  if (days <= 180) return "month";
  if (days <= 730) return "quarter";
  return "year";
}

/** Plotly `dtick` (ms) + tick format for a zoom scale. */
export function tickSpec(scale: ZoomScale): { dtick: number | string; format: string } {
  switch (scale) {
    case "day":
      return { dtick: DAY, format: "%d %b" };
    case "week":
      return { dtick: 7 * DAY, format: "%d %b" };
    case "month":
      return { dtick: "M1", format: "%b %Y" };
    case "quarter":
      return { dtick: "M3", format: "%b %Y" };
    case "year":
      return { dtick: "M12", format: "%Y" };
  }
}

/** Whether a track is the CPE spine (rig / frac / CT) — styled distinctly. */
export function isCpeTrack(track: PlanTimelineTrack): boolean {
  return track.kind === "rig" || track.kind === "frac" || track.kind === "ct";
}

/** True when the response has at least one dated bar to plot. */
export function hasBars(resp: PlanTimelineResponse | undefined): boolean {
  return !!resp && resp.tracks.some((t) => t.bars.length > 0);
}
